/**
 * Clustering service — shared types.
 * Mirrors SEO-SaaS/services/clustering/schemas.py + docs/FRONTEND_INTEGRATION.md.
 */

export type BatchStatus =
  | 'queued'
  | 'pending'
  | 'estimating'
  | 'awaiting_quota'
  | 'fetching_serp'
  | 'enriching'
  | 'normalizing'
  | 'clustering'
  | 'scoring'
  | 'labeling'
  | 'running'
  | 'done'
  | 'failed'
  | 'cancelled';

export const TERMINAL_BATCH_STATUSES: ReadonlySet<BatchStatus> = new Set([
  'done',
  'failed',
  'cancelled',
]);

export type SearchIntent =
  | 'informational'
  | 'navigational'
  | 'commercial investigation'
  | 'transactional';

export type FunnelStage = 'Top of Funnel' | 'Middle of Funnel' | 'Bottom of Funnel';

export interface BatchConfig {
  country: string;
  threshold: number;
  target_domain?: string | null;
  serp_source: 'auto' | 'crawler' | 'dataforseo';
  algorithm?: 'components' | 'auto' | 'leiden' | 'greedy';
  resolution?: number;
  depth: number;
  fetch_keyword_difficulty?: boolean;
}

export const DEFAULT_BATCH_CONFIG: BatchConfig = {
  country: 'United States',
  threshold: 2,
  target_domain: null,
  serp_source: 'auto',
  depth: 10,
};

export interface ProjectRead {
  id: string;
  name: string;
  description: string | null;
  settings: Record<string, unknown>;
  organization_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectCreate {
  name: string;
  description?: string | null;
  settings?: Record<string, unknown>;
}

export type ProjectUpdate = Partial<ProjectCreate>;

export interface BatchCreate {
  name?: string | null;
  project_id?: string | null;
  keywords: string[];
  config?: Partial<BatchConfig>;
}

export interface ReclusterRequest {
  name?: string | null;
  threshold?: number;
  target_domain?: string | null;
}

export interface BatchRead {
  id: string;
  project_id: string;
  name: string | null;
  status: BatchStatus;
  current_step: string | null;
  progress_pct: number;
  total_keywords: number;
  clusters_count: number;
  config: Record<string, unknown>;
  serp_cache_hits: number;
  serp_cache_misses: number;
  credits_estimated: number;
  credits_consumed: number;
  cost_usd_estimated: number;
  cost_usd_actual: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  parent_batch_id: string | null;
  created_at: string;
  updated_at: string;
}

export type BatchSummaryStatus = 'pending' | 'running' | 'done' | 'failed' | 'disabled';

export interface BatchSummaryPayload {
  executive_summary?: string;
  key_findings?: string[];
  content_opportunities?: Array<Record<string, unknown>>;
  cannibalization_risks?: Array<Record<string, unknown>>;
  competitor_insights?: string[];
  recommended_actions?: string[];
  priority_clusters?: Array<Record<string, unknown>>;
}

export interface BatchSummaryRead {
  id: string;
  batch_id: string;
  status: BatchSummaryStatus;
  model: string | null;
  payload: BatchSummaryPayload | null;
  markdown: string | null;
  error_message: string | null;
  generated_at: string | null;
  updated_at: string;
}

export interface TopCompetitor {
  domain: string;
  hits: number;
  pct_of_kws: number;
}

export interface ClusterRead {
  id: string;
  batch_id: string;
  cluster_label: string;
  cluster_label_source: 'hub_keyword' | 'llm_generated' | 'user_override';
  hub_keyword: string;
  keywords: string[];
  keywords_count: number;
  search_volume: number | null;
  google_ads_competition: number | null;
  seo_difficulty: number | null;
  serp_overlap: number | null;
  your_rank: number | null;
  expected_page_type: string | null;
  search_intent: SearchIntent | null;
  funnel_stage: FunnelStage | null;
  opportunity_score: number | null;
  cannibalization_risk: number | null;
  competitor_gap_score: number | null;
  top_competitors: TopCompetitor[];
  ai_overview_pct: number | null;
  serp_volatility_pct: number | null;
  is_user_renamed: boolean;
  renamed_at: string | null;
  is_merged: boolean;
  merged_into_id: string | null;
}

export interface EstimateRequest {
  keywords: string[];
  config?: Partial<BatchConfig>;
}

export interface EstimateResult {
  keywords_submitted: number;
  cache_hit_estimate: number;
  credits_required: number;
  usd_estimate: number;
  fetched_date: string;
}

export interface ProgressEvent {
  batch_id: string;
  status: BatchStatus;
  current_step: string | null;
  progress_pct: number;
  serp_cache_hits: number;
  serp_cache_misses: number;
  clusters_count?: number;
  ts: string;
  kind: 'snapshot' | 'progress' | 'complete';
}

export interface ClusterRename {
  cluster_label: string;
}

export interface ClustersListParams {
  search_intent?: SearchIntent;
  funnel_stage?: FunnelStage;
  min_volume?: number;
  include_merged?: boolean;
  sort?:
    | 'search_volume'
    | 'keywords_count'
    | 'opportunity_score'
    | 'cannibalization_risk'
    | 'competitor_gap_score'
    | 'ai_overview_pct';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface KeywordsListParams {
  search_intent?: SearchIntent;
  funnel_stage?: FunnelStage;
  sort?: 'search_volume' | 'ads_competition' | 'seo_difficulty' | 'cpc' | 'keyword';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface BatchesListParams {
  project_id?: string;
  limit?: number;
  offset?: number;
}

export type ExportFormat = 'csv' | 'json' | 'xlsx';

export interface KeywordRead {
  id: string;
  batch_id: string;
  keyword: string;
  normalized_keyword: string;
  search_volume: number | null;
  google_ads_competition: number | null;
  seo_difficulty: number | null;
  cpc: number | null;
  created_at: string;
}
